import React, { memo, useEffect, useState } from 'react';

// 🔴 Цвета категорий импортируются, а не копируются: «язык» на карте и
// «язык» в списке обязаны быть одного цвета, иначе это два разных навыка.
import { CATEGORY_COLORS, FALLBACK_COLOR } from 'atoms/SkillIcon/categoryColors';
// 🔴 Плитка — общее правило с HTML-значком: копия здесь означала бы, что один
// навык может выглядеть на карте и в списке двумя разными.
import { PLATE_LIGHT, plateFor } from 'atoms/SkillIcon/logoPlate';
import { SKILL_LOGOS } from 'atoms/SkillIcon/skillLogos';

/**
 * Знак технологии ВНУТРИ холста (ТЗ 0070).
 *
 * 🔴 Не `<SkillIcon>`. Тот рендерит HTML, а вложить HTML в SVG можно только
 * через `foreignObject` — и тогда ломается и масштабирование сцены, и печать
 * страницы. Здесь берутся ТЕ ЖЕ данные и в ТОМ ЖЕ порядке, что у `SkillIcon`:
 * картинка из ERP → встроенный знак по `slug` → буквенная плашка по
 * категории. Уровней именно три, а не два: у половины навыков справочника
 * знака нет вовсе (правообладатель запретил перераспространение — см.
 * `SkillIcon/NOTICE.md`), и оператор загружает картинку руками. Пропусти
 * первый уровень — и один навык выглядел бы на карте и в списке двумя
 * разными.
 *
 * 🔴 Знак стоит на ПЛИТКЕ; как выбирается её цвет и почему не по теме —
 * в `atoms/SkillIcon/logoPlate`. Правило закрыто тестом: у всех 77 знаков
 * контраст к своей плитке ≥ 3:1.
 */

interface ISkillGlyph {
  x: number;
  y: number;
  size: number;
  slug: string;
  name: string;
  /** Картинка из ERP. Старше встроенного знака: оператор знает лучше. */
  iconUrl?: string | null;
  category?: string | null;
}

/**
 * Первая БУКВА, а не первый символ: у «.NET» и «C#» первый символ — знак
 * препинания, и плашка получалась бы пустой точкой.
 */
const initialOf = (name: string): string =>
  ((name || '').replace(/[^\p{L}\p{N}]/gu, '').charAt(0) || '?').toUpperCase();

export const SkillGlyph = memo(({ x, y, size, slug, name, iconUrl, category }: ISkillGlyph) => {
  const logo = SKILL_LOGOS[slug];
  const radius = Math.round(size * 0.22);

  // 🔴 Битая ссылка обязана ОТКАТИТЬСЯ на встроенный знак, а не остаться
  // сломанной картинкой: объект могли удалить с бакета, и без отката карта из
  // шестидесяти узлов выглядела бы наполовину поломанной от одной мёртвой
  // ссылки. Сбрасываем при смене адреса, иначе новая картинка унаследовала бы
  // отказ старой.
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [iconUrl]);

  if (iconUrl && !broken) {
    return (
      <g transform={`translate(${x} ${y})`} aria-hidden focusable="false">
        <rect width={size} height={size} rx={radius} fill={PLATE_LIGHT} />
        {/* `meet` — это object-fit: contain: узкий логотип не растянется. */}
        <image
          href={iconUrl}
          width={size}
          height={size}
          preserveAspectRatio="xMidYMid meet"
          onError={() => setBroken(true)}
        />
      </g>
    );
  }

  if (logo) {
    // Путь нарисован в координатах 24×24 — приводим к стороне плитки.
    const inset = Math.round(size * 0.14);
    const scale = (size - inset * 2) / 24;
    return (
      <g transform={`translate(${x} ${y})`} aria-hidden focusable="false">
        <rect width={size} height={size} rx={radius} fill={plateFor(logo.color)} />
        <g transform={`translate(${inset} ${inset}) scale(${scale})`}>
          <path d={logo.d} fill={logo.color} />
        </g>
      </g>
    );
  }

  return (
    <g transform={`translate(${x} ${y})`} aria-hidden focusable="false">
      <rect
        width={size}
        height={size}
        rx={radius}
        fill={CATEGORY_COLORS[category || ''] || FALLBACK_COLOR}
      />
      <text
        x={size / 2}
        y={size / 2 + size * 0.18}
        textAnchor="middle"
        fill="#ffffff"
        fontSize={Math.round(size * 0.5)}
        fontWeight={600}>
        {initialOf(name)}
      </text>
    </g>
  );
});
